import { useMemo } from 'react'
import { createBreakpoint } from 'react-use'
import { cn } from '@/lib/utils'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  eventParticipantStatuses,
  unavailableParticipantStatuses,
} from 'app/frontend/types/custom/EventParticipantStatus'
import { CharacterRole } from 'app/frontend/types/custom'
import { characterRoles } from 'app/frontend/types/custom/CharacterRole'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import EventActivityRoleBlock from './EventActivityRoleBlock'

type Props = {
  activity: wowaudit.EventActivity
  applyToAllActivities?: boolean
}

const useColumnCount = createBreakpoint({
  four: 1000,
  three: 775,
  two: 550,
  one: 0,
})

const sortParticipantEntries = (
  entries: EventActivityParticipantEntry[],
  activityId: number,
) => {
  return entries.slice().sort((left, right) => {
    return resolveEventParticipantCharacter(
      left,
      activityId,
    ).name.localeCompare(
      resolveEventParticipantCharacter(right, activityId).name,
    )
  })
}

const EventActivityBlock = ({ activity, applyToAllActivities }: Props) => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const { allEntries } = useTeamEventParticipantsContext()
  const columnCount = useColumnCount()
  const canAdminEdit = accessLevel === 'admin'
  const selectableStatuses = useMemo(
    () =>
      eventParticipantStatuses.filter(
        (status) =>
          canAdminEdit ||
          team.settings.raidsAvailableStatuses.value.includes(status),
      ),
    [canAdminEdit, team.settings.raidsAvailableStatuses.value],
  )
  const entriesByRole = useMemo(() => {
    const grouped = new Map<
      CharacterRole,
      {
        selectedEntries: EventActivityParticipantEntry[]
        benchedEntries: EventActivityParticipantEntry[]
      }
    >(
      characterRoles.map((role) => [
        role,
        { selectedEntries: [], benchedEntries: [] },
      ]),
    )

    sortParticipantEntries(allEntries, activity.id).forEach((entry) => {
      const selection = entry.selections[activity.id]
      const group = grouped.get(selection.role as CharacterRole)
      if (!group) return
      const isUnavailable = unavailableParticipantStatuses.includes(
        entry.participant.status,
      )
      if (selection.selected && !isUnavailable) {
        group.selectedEntries.push(entry)
      } else {
        group.benchedEntries.push(entry)
      }
    })

    return grouped
  }, [activity.id, allEntries])

  const roleBlock = (role: CharacterRole) => {
    const group = entriesByRole.get(role) ?? {
      selectedEntries: [],
      benchedEntries: [],
    }

    return (
      <EventActivityRoleBlock
        role={role}
        selectedEntries={group.selectedEntries}
        benchedEntries={group.benchedEntries}
        activity={activity}
        selectableStatuses={selectableStatuses}
        canAdminEdit={canAdminEdit}
        applyToAllActivities={applyToAllActivities}
      />
    )
  }

  const roleLayout = () => {
    if (columnCount === 'four') {
      return (
        <div className='grid grid-cols-4 gap-3'>
          {roleBlock('tank')}
          {roleBlock('heal')}
          {roleBlock('melee')}
          {roleBlock('ranged')}
        </div>
      )
    }

    if (columnCount === 'three') {
      return (
        <div className='grid grid-cols-3 gap-3'>
          <div className='flex flex-col gap-3'>
            {roleBlock('tank')}
            {roleBlock('heal')}
          </div>
          {roleBlock('melee')}
          {roleBlock('ranged')}
        </div>
      )
    }

    if (columnCount === 'two') {
      return (
        <div className='grid grid-cols-2 gap-3'>
          <div className='flex flex-col gap-3'>
            {roleBlock('tank')}
            {roleBlock('heal')}
          </div>
          <div className='flex flex-col gap-3'>
            {roleBlock('melee')}
            {roleBlock('ranged')}
          </div>
        </div>
      )
    }

    return (
      <div className='flex flex-col gap-3'>
        {roleBlock('tank')}
        {roleBlock('heal')}
        {roleBlock('melee')}
        {roleBlock('ranged')}
      </div>
    )
  }

  const isInactive = !activity.active && !applyToAllActivities

  return (
    <div className='relative max-w-5xl mx-auto'>
      <div
        className={cn(
          'space-y-4',
          isInactive && 'pointer-events-none select-none opacity-30',
        )}
      >
        {roleLayout()}
      </div>
      {isInactive ? (
        <div className='absolute inset-0 flex items-center justify-center'>
          <span className='rounded-md border border-border bg-background px-4 py-2 font-medium backdrop-blur-sm'>
            This encounter is inactive
          </span>
        </div>
      ) : null}
    </div>
  )
}

export default EventActivityBlock
